/**
 * Pumpkin pose imports. Pose-to-UI-moment mapping is documented in
 * docs/pumpkin-prompts/ and CLAUDE.md. Screens render poses through
 * <PumpkinFigure pose="…" /> rather than reading `pumpkin` directly, so
 * seasonal extras (the Halloween bat) apply everywhere.
 *
 * Seasonal: for the whole of October `pumpkin` resolves to the Halloween
 * set in ./halloween/ (pumpkin costume) for every pose that has one; the
 * everyday art is back on 1 November. See src/lib/season.ts.
 *
 * `schoolUniform` is a later addition to the original eight — Pumpkin in
 * Eva's school uniform, for the "Hello, Year 2!" announcement modal. It was
 * generated from `cheer-celebrate.png` as a character reference so she stays
 * on-model; see scripts/gen_year2_assets.py.
 *
 * We ship the lossless WebP variants — byte-identical pixels to the source
 * PNGs but ~68% smaller. The 1024×1024 PNGs are kept in this folder as the
 * canonical source (regenerate WebPs via `node scripts/optimize-pumpkin-images.mjs`).
 */
import { isHalloweenSeason } from "@/lib/season";

import idleDefault from "./idle-default.webp";
import waveHello from "./wave-hello.webp";
import pointPresent from "./point-present.webp";
import listeningAlert from "./listening-alert.webp";
import cheerCelebrate from "./cheer-celebrate.webp";
import gentleEncourage from "./gentle-encourage.webp";
import presentSticker from "./present-sticker.webp";
import curledRest from "./curled-rest.webp";
import schoolUniform from "./school-uniform.webp";
import { halloweenPoses } from "./halloween";

const everyday = {
  idleDefault,
  waveHello,
  pointPresent,
  listeningAlert,
  cheerCelebrate,
  gentleEncourage,
  presentSticker,
  curledRest,
  schoolUniform,
} as const;

export type PumpkinPose = keyof typeof everyday;

/**
 * Decided once at load, from the device's local date. A tab left open
 * across midnight on 31 October keeps the costume until the next reload.
 */
export const halloweenActive = isHalloweenSeason(new Date());

export const pumpkin: Readonly<Record<PumpkinPose, string>> = halloweenActive
  ? { ...everyday, ...halloweenPoses }
  : everyday;
